// (c) chaehongee. Confidential and proprietary.

import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { store } from './store/store'
import { Analytics } from "@vercel/analytics/react"
import { SpeedInsights } from "@vercel/speed-insights/react"
import { AuthProvider } from './contexts/AuthContext'
import { analytics } from './services/analytics'
import { telemetry } from './services/telemetry'
import * as Sentry from "@sentry/react"
import { ErrorFallback } from './components/ErrorFallback'
import { HelmetProvider } from 'react-helmet-async'
import './i18n'
import './index.css'
import App from './App.tsx'
// startup handled by hooks

// Initialize Azure Application Insights
telemetry.initialize()

// Initialize Sentry
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "development",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.feedbackIntegration({
        autoInject: false,
        colorScheme: 'dark',
      }),
    ],
    // Performance monitoring
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || "0.1"),
    // Session replay (10% of sessions)
    replaysSessionSampleRate: 0.1,
    // Replay 100% of sessions with errors
    replaysOnErrorSampleRate: 1.0,
    // Release tracking
    release: import.meta.env.VITE_GIT_SHA || "development",
  });
}

// Initialize PostHog analytics
analytics.init()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog>
        <Provider store={store}>
          <AuthProvider>
            <Analytics />
            <SpeedInsights />
            <App />
          </AuthProvider>
        </Provider>
      </Sentry.ErrorBoundary>
    </HelmetProvider>
  </StrictMode>,
)
